/**
 * The self-service job editor (`/hm/jobs/:jobId/edit`, and the chat's side panel): a saved job's fields plus the live
 * signal rail, in the portal look. Jobs are created in chat. The page owns the `WorkflowDraft` (company, owner) and
 * this component passes it through. Automation changes on the Automation tab, except that a draft on a plan without
 * automation saves with it off, as `presetPatch` (the same mapping the Automation tab saves). The recruiter dashboard
 * keeps `JobSignalPanel`; both share the request mappers, submit hooks and `useRoleSignal`.
 */
import {
  AlertTriangle,
  Check,
  ChevronDown,
  HelpCircle,
  Pencil,
  Plus,
  X,
} from 'lucide-react';
import {
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/shadcn/utils';
import { PermissionsGate } from '@/modules/auth/components/permissions/PermissionsGate';
import { permissions } from '@/modules/auth/permissions';
import { useWorkspacePlan } from '@/modules/billing/hooks/useWorkspacePlan';
import { HMLoadError } from '@/modules/hiring-manager/components/HMLoadError';
import { ConnectLinkedInLink } from '@/modules/hiring-manager/linkedin/ConnectLinkedInLink';
import { ExampleCompaniesNote } from '@/modules/jobs/signal/components/ExampleCompaniesNote';
import { JobCountriesField } from '@/modules/jobs/signal/components/JobCountriesField';
import { JobEmployerIndustriesField } from '@/modules/jobs/signal/components/JobEmployerIndustriesField';
import { JobEmployerSizeField } from '@/modules/jobs/signal/components/JobEmployerSizeField';
import { JobSearchTitlesField } from '@/modules/jobs/signal/components/JobSearchTitlesField';
import { JobSlackChannelField } from '@/modules/jobs/signal/components/JobSlackChannelField';
import { SkillEmphasisMenu } from '@/modules/jobs/signal/components/SkillEmphasisMenu';
import {
  verdictChipClass,
  verdictDot,
} from '@/modules/jobs/signal/components/tone';
import {
  completenessLabelText,
  nextStepCopy,
  skillSufficiencyMessage,
  toScreeningQuestion,
  verdictShortText,
} from '@/modules/jobs/signal/derivations';
import { displayLabel, normalize } from '@/modules/jobs/signal/displayCasing';
import { emphasisOf, withEmphasis } from '@/modules/jobs/signal/emphasis';
import {
  missingForSubmit,
  type SubmitIntent,
} from '@/modules/jobs/signal/jobRequestMappers';
import { hasOfficeWork } from '@/modules/jobs/signal/location';
import {
  contractTypeOptions,
  type EmphasisLevel,
  emptyRole,
  type InboundDraft,
  isJobContractType,
  isJobLocationType,
  type JobSignal,
  locationTypeOptions,
  type NeedItem,
  type RoleDraft,
  seniorityOptions,
  type SkillVerdict,
  type TitleVerdict,
  type WorkflowDraft,
} from '@/modules/jobs/signal/types';
import {
  isSkillLintTermTooLong,
  SKILL_LINT_MAX_ITEM_LENGTH,
  useRoleSignal,
} from '@/modules/jobs/signal/useRoleSignal';
import {
  isStrongVerdict,
  verdictElicitation,
} from '@/modules/jobs/signal/verdict';
import { Button } from '@/modules/ui/components/button/Button';
import { ConfirmDialog } from '@/modules/ui/components/confirm-dialog/ConfirmDialog';
import { InlineError } from '@/modules/ui/components/inline-error/InlineError';
import { Input } from '@/modules/ui/components/input/Input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/modules/ui/components/popover/Popover';
import { Textarea } from '@/modules/ui/components/textarea/Textarea';
import { MultiSelect } from '@/modules/ui/MultiSelect';
import { useIsLinkedInConnected } from '@/modules/user-external-accounts/hooks/useIsLinkedInConnected';
import { useIsSlackConnected } from '@/modules/user-external-accounts/hooks/useIsSlackConnected';
import {
  limitToPlan,
  offersLinkedInConnect,
  presetPatch,
  recommendedPreset,
} from '@/pages/hm/job-automation/jobAutomation';

import { JobInboundFields } from './JobInboundFields';
import { SkillSearchInput } from './SkillSearchInput';
import type { EditorInbound } from './useEditorInbound';

export interface HMJobEditorProps {
  /** Hides Publish: an already-published job only saves (validated at publish level). */
  canPublish?: boolean;
  initialRole?: Partial<RoleDraft>;
  /**
   * Owned by the page (company, owner); passed back to `onSubmit`, with automation off for a draft on a plan
   * without it.
   */
  workflow: WorkflowDraft;
  onSubmit?: (
    role: RoleDraft,
    workflow: WorkflowDraft,
    intent: SubmitIntent,
    /** Only when `inbound` is passed: the section's values, saved with the job. */
    inbound?: InboundDraft,
  ) => void;
  /** Shows the applications section (dev-only, self-service); see `useEditorInbound`. */
  inbound?: EditorInbound | null;
  /** Where the applications section sends the user to turn on the Careers Page. */
  workspacePath?: string;
  /** disables Save/Publish while the page's mutation is in flight */
  submitting?: boolean;
  /** The page's last save or publish failure; shown above the form until the next attempt. */
  submitError?: string | null;
  /** Where the automation note links. */
  automationPath?: string;
  /** Where LinkedIn gets connected when outreach would wait for it, with the portal's search suffix. */
  integrationsPath: string;
  /** Stories: seed the signal and stop the hook from calling the AI endpoints. */
  signalEnabled?: boolean;
  initialVerdicts?: SkillVerdict[];
  initialTitleVerdict?: TitleVerdict;
  initialJobSignal?: JobSignal;
}

type ResolveAction = 'replace' | 'toQuestion';

const skillTooLongMessage = `Skills must be ${SKILL_LINT_MAX_ITEM_LENGTH} characters or fewer.`;

const dedupePush = (arr: string[], value: string) =>
  arr.some((x) => normalize(x) === normalize(value)) ? arr : [...arr, value];

/**
 * What publishing sets in motion, by how much the job hands to Finlay. With `connectLinkedIn`, outreach waits for the
 * user's LinkedIn, which they can connect now or later.
 */
const publishConsequence = (
  outreachOn: boolean,
  connectLinkedIn?: ReactNode,
) => {
  const starts = !outreachOn
    ? 'Finlay starts looking for candidates right away.'
    : connectLinkedIn
      ? 'Finlay starts looking for candidates right away. Your LinkedIn isn’t connected yet, so messages start as soon as you connect it.'
      : 'Finlay starts looking for candidates right away and messages candidates as it finds them.';
  const afterwards = outreachOn
    ? ' You can edit the job afterwards; changes shape upcoming messages and the search.'
    : ' You can edit the job afterwards; changes shape the search.';
  if (!outreachOn || !connectLinkedIn) return `${starts}${afterwards}`;
  return (
    <>
      {starts}
      {afterwards}
      <span className="mt-2 block">{connectLinkedIn}</span>
    </>
  );
};

export function HMJobEditor({
  canPublish = true,
  initialRole,
  workflow,
  onSubmit,
  submitting = false,
  submitError = null,
  automationPath,
  integrationsPath,
  inbound = null,
  workspacePath = '/hm/workspace',
  signalEnabled = true,
  initialVerdicts,
  initialTitleVerdict,
  initialJobSignal,
}: HMJobEditorProps) {
  const [role, setRole] = useState<RoleDraft>({ ...emptyRole, ...initialRole });
  // Seeded once, like the role: null keeps the job's inbound settings out of the request.
  const [inboundDraft, setInboundDraft] = useState<InboundDraft | null>(
    inbound?.initial ?? null,
  );
  // Kept even while the picker is hidden: null is the tenant channel or DM default.
  const [slackChannelId, setSlackChannelId] = useState<string | null>(
    workflow.slackChannelId ?? null,
  );
  const { isSlackConnected } = useIsSlackConnected();
  const { userId, linkedInStatus } = useIsLinkedInConnected();
  const [showMore, setShowMore] = useState(
    Boolean(
      initialRole?.companies ||
      initialRole?.exemplarUrl ||
      (initialRole?.employerSize ?? 'Any') !== 'Any' ||
      Boolean(initialRole?.employerIndustries?.length) ||
      Boolean(initialRole?.extraSearchTitles?.length),
    ),
  );
  const [confirmPublishOpen, setConfirmPublishOpen] = useState(false);
  // The example companies as last committed (on blur), which their note checks: a model call, so never per keystroke.
  const [checkedCompanies, setCheckedCompanies] = useState('');
  // Which button is waiting on the page's mutation: publishing takes seconds, so the pressed control owns the wait.
  const [pendingIntent, setPendingIntent] = useState<SubmitIntent | null>(null);
  // Set by the first blocked submit; the missing list then stays live so fixed fields drop off as the user fills them.
  const [checkIntent, setCheckIntent] = useState<SubmitIntent | null>(null);
  // A typed skill rejected before it reaches the list; cleared by the next accepted one.
  const [skillError, setSkillError] = useState<string | null>(null);

  const formRef = useRef<HTMLDivElement>(null);
  const missingAlertRef = useRef<HTMLDivElement>(null);
  const submitErrorRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);
  const locationRef = useRef<HTMLInputElement>(null);
  const companiesRef = useRef<HTMLInputElement>(null);
  const seniorityRef = useRef<HTMLDivElement>(null);

  const signal = useRoleSignal({
    role,
    enabled: signalEnabled,
    initialVerdicts,
    initialTitleVerdict,
    initialJobSignal,
  });

  const patch = (change: Partial<RoleDraft>) =>
    setRole((r) => ({ ...r, ...change }));
  const setEmphasis = (skill: string, level: EmphasisLevel | 'Normal') =>
    setRole((r) => ({
      ...r,
      skillEmphasis: withEmphasis(r.skillEmphasis, skill, level),
    }));

  const {
    plan,
    isLoading: isPlanLoading,
    error: planError,
    includesOutreach,
    includesAiPrescreen,
  } = useWorkspacePlan();
  const isManaged = plan === 'managed';
  // Only an explicit "not included" from Stigg removes automation; the API stays the real gate.
  const includesAutomation =
    includesOutreach !== false || includesAiPrescreen !== false;
  // The recommended preset holds choices the plan lacks (it is Autopilot), so drop them before saving.
  const effectiveAutomation = limitToPlan(recommendedPreset.values, {
    includesOutreach,
    includesAiPrescreen,
  });
  const missing = checkIntent
    ? missingForSubmit(role, workflow, checkIntent)
    : [];
  const isMissing = (key: string) => missing.includes(key);

  const submitBlocked =
    !onSubmit || submitting || isPlanLoading || Boolean(planError);
  // A draft on a plan without automation saves with it off. Managed jobs keep the backend defaults; recruiters
  // configure automation later.
  const appliesPreset = !isManaged && canPublish && !includesAutomation;
  const submitWorkflow: WorkflowDraft = {
    ...workflow,
    ...(appliesPreset
      ? {
          ...presetPatch(effectiveAutomation),
          // Shortlisting follows outreach: Finlay only contacts shortlisted candidates.
          isShortlistingAutomated: includesOutreach !== false,
        }
      : {}),
    slackChannelId,
  };
  // Publishing with outreach on means Finlay starts messaging people: the button and the confirm say so.
  const outreachOn = appliesPreset
    ? effectiveAutomation.outreach === 'finlay'
    : workflow.isReachOutAutomated === true;
  // Skippable: the API holds outreach until LinkedIn is connected, so publishing stays open.
  const offersLinkedIn =
    !isManaged &&
    offersLinkedInConnect({
      outreachOn,
      isOwner: userId !== undefined && workflow.ownerId === userId,
      linkedInStatus,
    });

  const emitSubmit = (intent: SubmitIntent) => {
    if (inboundDraft) onSubmit?.(role, submitWorkflow, intent, inboundDraft);
    else onSubmit?.(role, submitWorkflow, intent);
  };

  const submit = (intent: SubmitIntent) => {
    if (submitBlocked) return;
    // A published job validates at publish level even for "Save changes": a cleared description or must-have
    // list would otherwise slip through and the backend re-grounds the live search on it.
    const validationIntent: SubmitIntent = canPublish ? intent : 'publish';
    const list = missingForSubmit(role, workflow, validationIntent);
    setCheckIntent(list.length > 0 ? validationIntent : null);
    if (list.length > 0) {
      // Publish sits at both the top and the bottom of a long form: bring the first missing field into view
      // (after the render that marks it); fields the page owns (company) only surface in the summary alert.
      window.setTimeout(() => {
        const field = formRef.current?.querySelector('[data-missing]');
        (field ?? missingAlertRef.current)?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        field
          ?.querySelector<HTMLElement>('input, textarea, button')
          ?.focus({ preventScroll: true });
      });
      return;
    }
    // Publishing starts the search: confirm first.
    if (intent === 'publish') {
      setPendingIntent(null);
      setConfirmPublishOpen(true);
      return;
    }
    setPendingIntent(intent);
    emitSubmit(intent);
  };

  const focusNeed = (id: NeedItem['id']) => {
    const el =
      id === 'title'
        ? titleRef.current
        : id === 'location'
          ? locationRef.current
          : id === 'companies'
            ? companiesRef.current
            : seniorityRef.current;
    if (id === 'companies') setShowMore(true);
    // the companies input mounts on the next render when the section was collapsed
    window.setTimeout(() => {
      const target = id === 'companies' ? companiesRef.current : el;
      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (target instanceof HTMLInputElement) target.focus();
    });
  };

  const addSkill = (raw: string) => {
    const skill = raw.trim();
    if (!skill) return;
    if (isSkillLintTermTooLong(skill)) {
      setSkillError(skillTooLongMessage);
      return;
    }
    setSkillError(null);
    setRole((r) => ({ ...r, mustHave: dedupePush(r.mustHave, skill) }));
    signal.lintSkills([skill]);
  };

  const resolveSkill = (
    verdict: SkillVerdict,
    action: ResolveAction,
    value: string,
  ) => {
    const answer = value.trim();
    if (!answer) return;
    if (action === 'replace' && isSkillLintTermTooLong(answer)) {
      setSkillError(skillTooLongMessage);
      return;
    }
    setSkillError(null);
    setRole((r) => {
      const mustHave = r.mustHave.filter(
        (s) => normalize(s) !== normalize(verdict.input),
      );
      if (action === 'replace') {
        return { ...r, mustHave: dedupePush(mustHave, answer) };
      }
      return {
        ...r,
        mustHave,
        screeningQuestions: dedupePush(
          r.screeningQuestions,
          toScreeningQuestion(answer),
        ),
      };
    });
    if (action === 'replace') signal.lintSkills([answer]);
  };

  // Save and Publish also sit at the bottom of a long form: bring the failure into view.
  useEffect(() => {
    if (submitError) {
      submitErrorRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [submitError]);

  // A loaded job can predate the length limit; its skill is highlighted, so say why.
  const skillMessage =
    skillError ??
    (role.mustHave.some(isSkillLintTermTooLong)
      ? `${skillTooLongMessage} Shorten the highlighted skill.`
      : null);

  const strongLabels = signal.verdicts
    .filter((v) => isStrongVerdict(v.verdict))
    .map(displayLabel);
  const submitPermissions = [permissions.Jobs.Update];
  const publishPermissions = [
    permissions.Jobs.Update,
    permissions.Jobs.StartSourcing,
  ];

  // `submitting` alone cannot say which button to spin, and gating on it drops a stale intent once the
  // mutation ends — which also closes the publish confirmation, on success (we navigate) and on failure
  // (the error above the form explains, and the form is where the user fixes it).
  const saving = submitting && pendingIntent === 'save';
  const publishing = submitting && pendingIntent === 'publish';
  const publishConfirmOpen =
    confirmPublishOpen && (pendingIntent !== 'publish' || submitting);

  const actions = (
    <div className="flex shrink-0 gap-2">
      <PermissionsGate permissions={submitPermissions}>
        <Button
          size="sm"
          variant="outline"
          onClick={() => submit('save')}
          disabled={submitBlocked}
          loading={saving}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </PermissionsGate>
      {canPublish && (
        <PermissionsGate permissions={publishPermissions}>
          <Button
            size="sm"
            onClick={() => submit('publish')}
            disabled={submitBlocked}
            loading={publishing}
          >
            {publishing
              ? isManaged
                ? 'Publishing…'
                : 'Starting sourcing…'
              : isManaged
                ? 'Publish'
                : 'Start sourcing'}
          </Button>
        </PermissionsGate>
      )}
    </div>
  );

  return (
    // Laid out by its own width, not the window's: the chat's side panel shows it in about 500px on a wide screen.
    <div className="@container flex flex-col gap-8">
      <ConfirmDialog
        open={publishConfirmOpen}
        onOpenChange={setConfirmPublishOpen}
        title={isManaged ? 'Start the search?' : 'Start sourcing?'}
        description={
          isManaged
            ? 'Finlay starts looking for candidates. Your Finlay recruiter manages this job and any changes after creation.'
            : publishConsequence(
                outreachOn,
                offersLinkedIn && (
                  // A new tab keeps this draft: connecting leaves for LinkedIn's sign-in.
                  <ConnectLinkedInLink newTab to={integrationsPath} />
                ),
              )
        }
        confirmText={isManaged ? 'Publish' : 'Start sourcing'}
        confirmingText={isManaged ? 'Publishing…' : 'Starting sourcing…'}
        confirming={publishing}
        onConfirm={() => {
          if (submitBlocked) return;
          setPendingIntent('publish');
          emitSubmit('publish');
        }}
      />

      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-[22px] leading-7 font-semibold tracking-[-0.01em]">
            Edit job
          </h1>
          <p className="text-muted-foreground mt-1 text-[13px]">
            Describe the role. Finlay tells you what still sharpens the search
            as you go. Fields marked <span className="text-destructive">*</span>{' '}
            are required.
          </p>
        </div>
        {actions}
      </header>

      {planError && (
        <HMLoadError
          title="Workspace plan unavailable"
          description="We could not confirm your workspace plan. Please reload the page before saving this job."
        />
      )}

      {submitError && (
        <div ref={submitErrorRef}>
          <HMLoadError description={submitError} />
        </div>
      )}

      {missing.length > 0 && (
        <div
          ref={missingAlertRef}
          role="alert"
          className="border-semantic-540/50 bg-semantic-540/10 flex items-start gap-2 rounded-lg border p-3 text-[13px]"
        >
          <AlertTriangle className="text-semantic-540 mt-0.5 size-4 shrink-0" />
          <span>
            Still missing:{' '}
            <span className="font-medium">{missing.join(', ')}</span>.
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 gap-10 @2xl:grid-cols-[1fr_300px]">
        <div ref={formRef} className="flex min-w-0 flex-col">
          <Section title="About the job" first>
            <Field
              label="Job title"
              htmlFor="job-title"
              required
              missing={isMissing('title')}
            >
              <Input
                id="job-title"
                ref={titleRef}
                autoFocus
                aria-required
                placeholder="Senior Backend Engineer"
                value={role.title}
                onChange={(e) => patch({ title: e.target.value })}
                onBlur={(e) => signal.lintTitle(e.target.value)}
              />
              {signal.titleVerdict === 'vague' && role.title.trim() && (
                <Hint tone="warn">
                  Too broad to search on. Add a specialism, like a stack,
                  domain, or function.
                </Hint>
              )}
            </Field>
            <Field
              label="Description"
              htmlFor="job-description"
              required
              missing={isMissing('description')}
              hint="What the person will own, the team, the stack, and what good looks like."
            >
              <Textarea
                id="job-description"
                rows={7}
                aria-required
                placeholder="You will own our payments platform end to end…"
                value={role.description}
                onChange={(e) => patch({ description: e.target.value })}
              />
            </Field>
          </Section>

          <Section
            title="Skills"
            description="Must-haves drive the search. Finlay checks each one and flags the ones it cannot search for. A skill's arrows make the search lean on it more or less."
          >
            <Field
              label="Must-have skills"
              required
              missing={isMissing('a must-have skill')}
            >
              <SkillSearchInput
                onCommit={addSkill}
                placeholder="Add a must-have skill"
                required
              />
              {skillMessage && <InlineError>{skillMessage}</InlineError>}
              <SkillChips
                skills={role.mustHave}
                verdictCache={signal.verdictCache}
                onRemove={(s) =>
                  patch({
                    mustHave: role.mustHave.filter((x) => x !== s),
                    skillEmphasis: withEmphasis(
                      role.skillEmphasis,
                      s,
                      'Normal',
                    ),
                  })
                }
                onResolve={resolveSkill}
                emphasis={role.skillEmphasis}
                onEmphasis={setEmphasis}
              />
              {signal.pendingCount > 0 && (
                <p className="text-muted-foreground text-[13px]">
                  Checking {signal.pendingCount} skill
                  {signal.pendingCount === 1 ? '' : 's'}…
                </p>
              )}
              {signal.failedSkills.length > 0 && (
                <div className="flex items-center justify-between gap-2 text-[13px]">
                  <span className="text-destructive">
                    Couldn&apos;t check {signal.failedSkills.length} skill
                    {signal.failedSkills.length === 1 ? '' : 's'}.
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => signal.lintSkills(signal.failedSkills)}
                  >
                    Try again
                  </Button>
                </div>
              )}
              {signal.verdicts.length > 0 && (
                <Hint tone={signal.sufficiencyLevel === 'ok' ? 'ok' : 'warn'}>
                  {skillSufficiencyMessage(
                    signal.sufficiencyLevel,
                    strongLabels,
                  )}
                </Hint>
              )}
            </Field>
            <Field label="Nice-to-have skills" optional>
              <SkillSearchInput
                onCommit={(raw) =>
                  raw.trim() &&
                  patch({ niceToHave: dedupePush(role.niceToHave, raw.trim()) })
                }
                placeholder="Add a nice-to-have skill"
              />
              <SkillChips
                skills={role.niceToHave}
                onRemove={(s) =>
                  patch({
                    niceToHave: role.niceToHave.filter((x) => x !== s),
                    skillEmphasis: withEmphasis(
                      role.skillEmphasis,
                      s,
                      'Normal',
                    ),
                  })
                }
                emphasis={role.skillEmphasis}
                onEmphasis={setEmphasis}
              />
            </Field>
          </Section>

          <Section title="Where and who">
            <div className="grid grid-cols-1 gap-5 @md:grid-cols-2">
              <Field label="Seniority">
                <div ref={seniorityRef}>
                  <MultiSelect
                    options={seniorityOptions}
                    defaultValue={role.seniority}
                    onValueChange={(v) => patch({ seniority: v })}
                    placeholder="Any level"
                    hideSelectAll
                    maxCount={3}
                    className="h-auto min-h-9"
                  />
                </div>
              </Field>
              <Field
                label="Location"
                htmlFor="job-location"
                required
                missing={isMissing('location')}
              >
                <Input
                  id="job-location"
                  ref={locationRef}
                  aria-required
                  aria-describedby="job-location-help"
                  placeholder={
                    hasOfficeWork(role.locationTypes)
                      ? 'Prague, Czechia'
                      : 'Germany, Austria, Switzerland'
                  }
                  value={role.location}
                  onChange={(e) => patch({ location: e.target.value })}
                />
                <p
                  id="job-location-help"
                  className="text-muted-foreground text-[13px]"
                >
                  Remote: where a hire may work. Onsite or hybrid: enter the
                  office location.
                </p>
              </Field>
              <Field
                label="Work arrangement"
                required
                missing={isMissing('work arrangement')}
              >
                <MultiSelect
                  options={locationTypeOptions}
                  defaultValue={role.locationTypes}
                  onValueChange={(v) =>
                    patch({ locationTypes: v.filter(isJobLocationType) })
                  }
                  placeholder="Onsite, hybrid, or remote"
                  aria-required
                  hideSelectAll
                  maxCount={3}
                  className="h-auto min-h-9"
                />
              </Field>
              <Field
                label="Employment type"
                required
                missing={isMissing('employment type')}
              >
                <MultiSelect
                  options={contractTypeOptions}
                  defaultValue={role.contractTypes}
                  onValueChange={(v) =>
                    patch({ contractTypes: v.filter(isJobContractType) })
                  }
                  placeholder="Employment or B2B"
                  aria-required
                  hideSelectAll
                  maxCount={2}
                  className="h-auto min-h-9"
                />
              </Field>
            </div>
            <div className="grid grid-cols-1 gap-5 @md:grid-cols-2">
              <Field
                label="Countries"
                required
                missing={isMissing('countries')}
              >
                <JobCountriesField
                  countries={role.countries}
                  isAnywhere={role.isAnywhere}
                  locationTypes={role.locationTypes}
                  onChange={patch}
                  helpClassName="text-muted-foreground text-[13px]"
                />
              </Field>
              {hasOfficeWork(role.locationTypes) && (
                <Field label="Office city" htmlFor="job-office-city" optional>
                  <Input
                    id="job-office-city"
                    aria-describedby="job-office-city-help"
                    placeholder="Prague"
                    value={role.targetCity}
                    onChange={(e) => patch({ targetCity: e.target.value })}
                  />
                  <p
                    id="job-office-city-help"
                    className="text-muted-foreground text-[13px]"
                  >
                    Add a city when the role has a single office.
                  </p>
                </Field>
              )}
            </div>
          </Section>

          <Section
            title="Screening questions"
            description="Finlay asks these on the pre-screen call. Fixing a vague skill above adds one here, and you can add your own."
          >
            <ScreeningQuestions
              questions={role.screeningQuestions}
              onAdd={(q) =>
                patch({
                  screeningQuestions: dedupePush(role.screeningQuestions, q),
                })
              }
              onRemove={(i) =>
                patch({
                  screeningQuestions: role.screeningQuestions.filter(
                    (_, index) => index !== i,
                  ),
                })
              }
              onSave={(i, text) =>
                patch({
                  screeningQuestions: role.screeningQuestions.map((q, index) =>
                    index === i ? text : q,
                  ),
                })
              }
            />
          </Section>

          {isSlackConnected && (
            <Section title="Notifications" optional>
              <JobSlackChannelField
                value={slackChannelId}
                currentName={workflow.slackChannelName}
                onChange={setSlackChannelId}
              />
            </Section>
          )}

          <Section title="Help Finlay search better" optional>
            {showMore ? (
              <div className="flex flex-col gap-5">
                <Field
                  label="Example companies"
                  htmlFor="job-companies"
                  hint="Companies known for great people in this role."
                >
                  <Input
                    id="job-companies"
                    ref={companiesRef}
                    placeholder="Stripe, Datadog"
                    value={role.companies}
                    onChange={(e) => patch({ companies: e.target.value })}
                    onBlur={(e) => setCheckedCompanies(e.target.value.trim())}
                  />
                  {checkedCompanies === role.companies.trim() ? (
                    <ExampleCompaniesNote
                      companies={checkedCompanies}
                      employerSize={role.employerSize ?? 'Any'}
                      employerIndustries={role.employerIndustries ?? []}
                      companyId={workflow.companyId}
                      className="text-muted-foreground text-[13px]"
                    />
                  ) : null}
                </Field>
                <Field label="Extra search titles">
                  <JobSearchTitlesField
                    value={role.extraSearchTitles}
                    onChange={(titles) => patch({ extraSearchTitles: titles })}
                    helpClassName="text-muted-foreground text-[13px]"
                  />
                </Field>
                <Field label="Candidates' company size">
                  <JobEmployerSizeField
                    value={role.employerSize ?? 'Any'}
                    onChange={(band) => patch({ employerSize: band })}
                    helpClassName="text-muted-foreground text-[13px]"
                  />
                </Field>
                <Field label="Candidates' industries">
                  <JobEmployerIndustriesField
                    value={role.employerIndustries ?? []}
                    onChange={(industries) =>
                      patch({ employerIndustries: industries })
                    }
                    helpClassName="text-muted-foreground text-[13px]"
                  />
                </Field>
                <Field
                  label="A great hire looks like"
                  htmlFor="job-exemplar"
                  hint="Paste the LinkedIn profile of someone who would be perfect."
                >
                  <Input
                    id="job-exemplar"
                    placeholder="https://linkedin.com/in/…"
                    value={role.exemplarUrl}
                    onChange={(e) => patch({ exemplarUrl: e.target.value })}
                  />
                </Field>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowMore(true)}
                className="text-primary-700 flex w-fit cursor-pointer items-center gap-1 text-[13px] font-medium"
              >
                Add details about who to find
                <ChevronDown className="size-3.5" />
              </button>
            )}
          </Section>

          {inbound && inboundDraft && (
            <Section
              title="Job applications"
              description="Let people apply from your Careers Page, alongside the candidates Finlay finds."
            >
              <JobInboundFields
                value={inboundDraft}
                onChange={setInboundDraft}
                isCareersPageEnabled={inbound.isCareersPageEnabled}
                workspacePath={workspacePath}
              />
            </Section>
          )}

          <div className="flex flex-col gap-4 border-t pt-6">
            {!isManaged && automationPath && (
              <p className="text-muted-foreground max-w-[64ch] text-[13px] leading-[18px]">
                How Finlay runs this search lives under{' '}
                <Link
                  className="text-foreground font-medium hover:underline"
                  to={automationPath}
                >
                  Automation
                </Link>
                .
              </p>
            )}
            {actions}
          </div>
        </div>

        <aside className="@2xl:sticky @2xl:top-6 @2xl:self-start">
          <div className="flex flex-col gap-5 rounded-lg border p-4">
            <div>
              <div className="flex items-center justify-between">
                <span className="text-[13px] font-semibold">
                  How searchable is this job?
                </span>
                <span className="text-muted-foreground text-xs capitalize">
                  {signal.completeness
                    ? completenessLabelText(signal.completeness.label)
                    : '…'}
                </span>
              </div>
              <StrengthBar value={signal.completeness?.strength ?? 0} />
              <p className="text-muted-foreground mt-2 text-[13px]">
                {!signal.completeness ? (
                  signal.signalError ? (
                    "Couldn't check the signal right now."
                  ) : (
                    'Checking…'
                  )
                ) : signal.completeness.nextStep ? (
                  <>
                    Next:{' '}
                    <span className="text-foreground font-medium">
                      {nextStepCopy(signal.completeness.nextStep)}
                    </span>
                  </>
                ) : signal.completeness.label === 'strong' ? (
                  <span className="text-semantic-560 font-medium">
                    Ready to start sourcing.
                  </span>
                ) : null}
              </p>
            </div>

            <div className="border-t" />

            <div>
              <span className="text-[13px] font-semibold">Still needed</span>
              <ul className="mt-2 flex flex-col gap-1.5">
                {signal.needs.map((need) => (
                  <NeedRow key={need.id} need={need} onFocus={focusNeed} />
                ))}
              </ul>
            </div>

            <div className="border-t" />

            <div>
              <span className="text-[13px] font-semibold">Skills check</span>
              {signal.verdicts.length === 0 ? (
                <p className="text-muted-foreground mt-2 text-[13px]">
                  Add must-have skills and Finlay checks it can search for them.
                </p>
              ) : (
                <ul className="mt-2 flex flex-col gap-1.5 text-[13px]">
                  {signal.verdicts.map((v) => {
                    const strong = isStrongVerdict(v.verdict);
                    return (
                      <li key={v.input} className="flex items-center gap-2">
                        {strong ? (
                          <Check className="text-semantic-560 size-3.5 shrink-0" />
                        ) : (
                          <AlertTriangle className="text-semantic-540 size-3.5 shrink-0" />
                        )}
                        <span className="truncate">{displayLabel(v)}</span>
                        <span className="text-muted-foreground ml-auto shrink-0 text-xs">
                          {verdictShortText(v.verdict)}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

/* ── layout bits ─────────────────────────────────────────────────────────── */

function Section({
  title,
  description,
  optional,
  first,
  children,
}: {
  title: string;
  description?: string;
  optional?: boolean;
  first?: boolean;
  children: ReactNode;
}) {
  return (
    <section className={cn('flex flex-col gap-5 py-8', !first && 'border-t')}>
      <div>
        <h2 className="text-[13px] leading-[18px] font-semibold">
          {title}
          {optional && (
            <span className="ml-1.5 font-normal text-neutral-500">
              Optional
            </span>
          )}
        </h2>
        {description && (
          <p className="text-muted-foreground mt-0.5 max-w-[56ch] text-[13px] leading-[18px]">
            {description}
          </p>
        )}
      </div>
      {children}
    </section>
  );
}

function Field({
  label,
  hint,
  htmlFor,
  optional,
  required,
  missing,
  children,
}: {
  label: string;
  hint?: string;
  htmlFor?: string;
  optional?: boolean;
  /** Required to publish; the asterisk is hidden from the accessible name, the controls carry `aria-required`. */
  required?: boolean;
  /** marks the field after a blocked submit; `submit` scrolls to the first `data-missing` field */
  missing?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5" data-missing={missing || undefined}>
      <label htmlFor={htmlFor} className="font-medium">
        {label}
        {required && (
          <span className="text-destructive ml-0.5" aria-hidden>
            *
          </span>
        )}
        {optional && (
          <span className="ml-1.5 text-[13px] font-normal text-neutral-500">
            Optional
          </span>
        )}
      </label>
      {hint && (
        <p className="text-muted-foreground -mt-1 text-[13px]">{hint}</p>
      )}
      {children}
      {missing && <Hint tone="warn">Required.</Hint>}
    </div>
  );
}

function Hint({
  tone,
  children,
}: {
  tone: 'ok' | 'warn';
  children: ReactNode;
}) {
  return (
    <p
      className={cn(
        'flex items-start gap-1.5 text-[13px] leading-[18px]',
        tone === 'ok' ? 'text-semantic-560' : 'text-neutral-600',
      )}
    >
      {tone === 'ok' ? (
        <Check className="mt-0.5 size-3.5 shrink-0" />
      ) : (
        <AlertTriangle className="text-semantic-540 mt-0.5 size-3.5 shrink-0" />
      )}
      <span>{children}</span>
    </p>
  );
}

function StrengthBar({ value }: { value: number }) {
  const color =
    value >= 0.75
      ? 'bg-semantic-560'
      : value >= 0.45
        ? 'bg-semantic-540'
        : 'bg-neutral-400';
  return (
    <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-neutral-200">
      <div
        className={cn('h-full rounded-full transition-all duration-500', color)}
        style={{ width: `${Math.round(value * 100)}%` }}
      />
    </div>
  );
}

function NeedRow({
  need,
  onFocus,
}: {
  need: NeedItem;
  onFocus: (id: NeedItem['id']) => void;
}) {
  if (need.status === 'done') {
    return (
      <li className="flex items-center gap-2 text-[13px]">
        <Check className="text-semantic-560 size-3.5 shrink-0" />
        <span className="text-muted-foreground">{need.label}</span>
      </li>
    );
  }
  const optional = need.status === 'optional';
  return (
    <li>
      <button
        type="button"
        onClick={() => onFocus(need.id)}
        className="hover:bg-muted -mx-2 flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] transition-colors"
      >
        {optional ? (
          <Plus className="size-3.5 shrink-0 text-neutral-500" />
        ) : (
          <AlertTriangle className="text-semantic-540 size-3.5 shrink-0" />
        )}
        <span className="flex-1 font-medium">
          {need.label}
          {optional && (
            <span className="ml-1 font-normal text-neutral-500">optional</span>
          )}
        </span>
        <span className="text-primary-700 text-xs font-medium">Add</span>
      </button>
    </li>
  );
}

/* ── skills ──────────────────────────────────────────────────────────────── */

function SkillChips({
  skills,
  verdictCache,
  onRemove,
  onResolve,
  emphasis,
  onEmphasis,
}: {
  skills: string[];
  /** When present, chips carry a verdict dot and a Fix popover for flagged ones. */
  verdictCache?: Record<string, SkillVerdict>;
  onRemove: (skill: string) => void;
  onResolve?: (
    verdict: SkillVerdict,
    action: ResolveAction,
    value: string,
  ) => void;
  /** How much the search leans on each skill, with an arrow on each chip to change it. */
  emphasis?: Record<string, EmphasisLevel>;
  onEmphasis?: (skill: string, level: EmphasisLevel | 'Normal') => void;
}) {
  if (skills.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {skills.map((skill) => {
        const verdict = verdictCache?.[normalize(skill)];
        const flagged = verdict ? !isStrongVerdict(verdict.verdict) : false;
        return (
          <span
            key={skill}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border py-1 pr-1 pl-2.5 text-[13px]',
              verdict ? verdictChipClass(verdict.verdict) : 'border-input',
            )}
          >
            {verdictCache &&
              (verdict ? (
                <span
                  className={cn(
                    'size-1.5 rounded-full',
                    verdictDot(verdict.verdict),
                  )}
                />
              ) : (
                <span className="size-1.5 animate-pulse rounded-full bg-neutral-500" />
              ))}
            <span>{verdict ? displayLabel(verdict) : skill}</span>
            {flagged && verdict && onResolve && (
              <FixPopover verdict={verdict} onResolve={onResolve} />
            )}
            {emphasis && onEmphasis && (
              <SkillEmphasisMenu
                skill={verdict ? displayLabel(verdict) : skill}
                level={emphasisOf(emphasis, skill)}
                onChange={(level) => onEmphasis(skill, level)}
              />
            )}
            <button
              type="button"
              onClick={() => onRemove(skill)}
              aria-label={`Remove ${skill}`}
              className="hover:text-foreground rounded-full p-0.5 text-neutral-500"
            >
              <X className="size-3" />
            </button>
          </span>
        );
      })}
    </div>
  );
}

function FixPopover({
  verdict,
  onResolve,
}: {
  verdict: SkillVerdict;
  onResolve: (
    verdict: SkillVerdict,
    action: ResolveAction,
    value: string,
  ) => void;
}) {
  const [open, setOpen] = useState(false);
  const [answer, setAnswer] = useState('');
  const isRecover = verdictElicitation(verdict.verdict) === 'recover';
  const action: ResolveAction = isRecover ? 'replace' : 'toQuestion';
  const title = isRecover
    ? `Finlay does not recognise “${verdict.input}”.`
    : verdict.verdict === 'ubiquitous'
      ? `“${verdict.input}” matches almost everyone.`
      : `“${verdict.input}” is a trait, not a searchable skill.`;
  const body = isRecover
    ? 'What is the closest public tool or skill? Finlay searches for that instead.'
    : 'What do you actually need? Finlay turns it into a question for the pre-screen call.';
  const commit = () => {
    if (!answer.trim()) return;
    onResolve(verdict, action, answer);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="text-semantic-540 hover:bg-semantic-540/15 rounded-full px-1.5 text-xs font-medium"
        >
          Fix
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80">
        <p className="font-medium">{title}</p>
        <p className="text-muted-foreground mt-1 text-[13px]">{body}</p>
        <div className="mt-3 flex gap-2">
          <Input
            autoFocus
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            placeholder={isRecover ? 'Redux' : 'mentoring junior engineers'}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commit();
              }
            }}
          />
          <Button size="sm" disabled={!answer.trim()} onClick={commit}>
            {isRecover ? 'Use this' : 'Make it a question'}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/* ── screening questions ─────────────────────────────────────────────────── */

function ScreeningQuestions({
  questions,
  onAdd,
  onRemove,
  onSave,
}: {
  questions: string[];
  onAdd: (question: string) => void;
  onRemove: (index: number) => void;
  onSave: (index: number, text: string) => void;
}) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [draft, setDraft] = useState('');

  // Removing a row above the one being edited shifts the list; keep the edit on the same question.
  const removeAt = (index: number) => {
    onRemove(index);
    setEditingIndex((current) => {
      if (current === null || current < index) return current;
      return current === index ? null : current - 1;
    });
  };

  const commitDraft = () => {
    const text = draft.trim();
    if (!text) return;
    onAdd(text);
    setDraft('');
  };

  return (
    <div className="flex flex-col gap-3">
      {questions.length > 0 && (
        <ul className="flex flex-col gap-1.5">
          {/* keyed by text (unique, see dedupePush) so the editor stays with its question when rows shift */}
          {questions.map((q, i) =>
            editingIndex === i ? (
              <QuestionEditor
                key={q}
                initial={q}
                onCancel={() => setEditingIndex(null)}
                onSave={(text) => {
                  onSave(i, text);
                  setEditingIndex(null);
                }}
              />
            ) : (
              <li key={q} className="group flex items-start gap-2 text-[13px]">
                <HelpCircle className="text-primary-700 mt-0.5 size-3.5 shrink-0" />
                <span className="flex-1">{q}</span>
                <span className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                  <button
                    type="button"
                    onClick={() => setEditingIndex(i)}
                    aria-label="Edit question"
                    className="hover:text-foreground rounded p-0.5 text-neutral-500"
                  >
                    <Pencil className="size-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeAt(i)}
                    aria-label="Remove question"
                    className="hover:text-foreground rounded p-0.5 text-neutral-500"
                  >
                    <X className="size-3.5" />
                  </button>
                </span>
              </li>
            ),
          )}
        </ul>
      )}
      <div className="flex gap-2">
        <Input
          aria-label="New screening question"
          placeholder="Have you led a migration to Kubernetes?"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitDraft();
            }
          }}
        />
        <Button
          size="sm"
          variant="outline"
          disabled={!draft.trim()}
          onClick={commitDraft}
        >
          Add
        </Button>
      </div>
    </div>
  );
}

function QuestionEditor({
  initial,
  onSave,
  onCancel,
}: {
  initial: string;
  onSave: (text: string) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState(initial);
  const commit = () => {
    const trimmed = text.trim();
    if (trimmed) onSave(trimmed);
    else onCancel();
  };
  return (
    <li className="flex items-center gap-2">
      <Input
        autoFocus
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          }
          if (e.key === 'Escape') onCancel();
        }}
      />
      <Button size="sm" onClick={commit}>
        Save
      </Button>
    </li>
  );
}
